/**
 * 中文翻译覆盖率的**可核对**审计（纯函数，不联网）。
 *
 * ## 为什么需要这么一份报告
 *
 * 上游 master 的 pdef 参数名与发布版固件**不一致**：核对上游文件时发现 master 给一些参数
 * 加了单位后缀（`RTL_ALT` → `RTL_ALT_M`、`RTL_SPEED` → `RTL_SPEED_MS`），
 * 而发布版固件（4.6/4.7）用的还是老名字。译表（内置种子 + 调用方注入）按发布版固件的名字写，
 * 于是对着 master 的 pdef 建索引时这些条目**一条都匹配不上**。
 *
 * 匹配不上的后果不是报错，而是**界面上悄悄少几个中文说明**：参数照常显示、照常读写，
 * 只是没有中文。没有任何外在症状，也没有任何日志 —— 所以必须有一样东西把它**显式**报出来，
 * 并且能**反复跑**（改了译表、或上游又改名之后，重跑一次就能看出差别）。
 *
 * ## 怎么用（把上游 pdef 抓下来核对）
 *
 * 这里刻意只做纯函数，联网留给 `tools/` 下的脚本或 CI 任务 ——
 * 于是比对规则本身能被固定 fixture 单测覆盖，不受网络影响。接一个脚本的样子：
 *
 * ```ts
 * const response = await createFetchParamFetcher()(paramMetadataUrl('Copter'));
 * if (!response.ok) throw new Error(`未能核对：HTTP ${response.status}`);
 * const index = createParameterMetadataIndex(await response.text(), { source: 'network' });
 * const audit = auditTranslationCoverage(index, 应用层注入的译表);
 * for (const item of audit.suspectedRenames) {
 *   console.log(`${item.translationKey} → ${item.upstreamName}（疑似 ${item.direction}）`);
 * }
 * ```
 *
 * 报告怎么读：
 *
 * - `status: 'unverified'` —— **未能核对**（拿不到 pdef）。此时**不要**照这份报告改译表，
 *   原因见最后一节。
 * - `suspectedRenames` —— 上游多半只是改了名。按 `upstreamName` 补一条译表项即可；
 *   长期方案是让译表按固件版本切换（见 `metadata.ts` 顶部对种子表的说明）。
 * - `staleTranslations` —— 译表里有、上游却没有，而且**不像**只是改名：参数被上游删掉了，
 *   或者译名本身写错。这一类要人看一眼再决定是删还是改。
 * - `untranslated` —— 有元数据但没有中文的参数名。上游每加一个新参数就会出现在这里，
 *   缓慢增长是正常的；突然多出一大批才意味着译表与固件版本对不上了。
 *
 * ## 网络不可用时必须"如实报告未能核对"
 *
 * 拿不到 pdef 时索引是空的（`source === 'empty'`、`size === 0`）。若照常比对，
 * **整张译表都会被报成"参数没了"**：一次网络抖动就刷出几百条改名嫌疑，
 * 于是这份报告再也没人看 —— 这正是"降级路径反而制造假警报"的典型。
 * 所以这种情况下返回 `status: 'unverified'` 并带上索引自报的原因，一个字都不猜。
 */
import {
  mergeParamTranslations,
  type ParamMetadataIndex,
  type ParamMetadataSourceStatus,
  type ParamTranslations,
} from './metadata';

export type TranslationAuditStatus =
  /** 拿到了 pdef，比对结论有效 */
  | 'verified'
  /** 拿不到 pdef（离线 / 上游挂掉）—— 只是"未能核对"，结论一律为空 */
  | 'unverified';

export type ParamRenameDirection =
  /** 上游给参数名**加了**单位后缀：译表的键比上游名短（`RTL_ALT` → `RTL_ALT_M`） */
  | 'upstream-added-suffix'
  /** 上游把单位后缀**去掉**了：译表的键比上游名长（`RTL_ALT_M` → `RTL_ALT`） */
  | 'upstream-dropped-suffix';

export interface ParamRenameSuspicion {
  /** 译表里的键（发布版固件的参数名） */
  readonly translationKey: string;
  /** 上游 pdef 里的参数名 */
  readonly upstreamName: string;
  /** 让两者不一致的单位后缀，例如 `_M` */
  readonly suffix: string;
  readonly direction: ParamRenameDirection;
}

export interface TranslationCoverageAudit {
  readonly status: TranslationAuditStatus;
  /** 索引自报的来源，直接透出来，便于判断这份结论值不值得信 */
  readonly source: ParamMetadataSourceStatus;
  /** `status === 'unverified'` 的原因（索引自报的 `unavailableReason`），否则为 null */
  readonly unverifiedReason: string | null;
  /** 参与比对的参数条数（= 索引大小） */
  readonly paramCount: number;
  /** 其中有中文说明的条数 */
  readonly translatedCount: number;
  /** 有元数据、但没有中文说明的参数名（界面只能退回英文说明） */
  readonly untranslated: readonly string[];
  /** 译表里有、索引里没有、且不像只是改名的键（参数被删掉，或译名写错） */
  readonly staleTranslations: readonly string[];
  /** 疑似只是上游改了名的条目 —— `RTL_ALT` → `RTL_ALT_M` 这类漂移真正的载体 */
  readonly suspectedRenames: readonly ParamRenameSuspicion[];
  /** 索引里有、但译表里没有中文标签的分组 id */
  readonly untranslatedGroups: readonly string[];
  /** 译表里有、索引里没有的分组 id */
  readonly staleGroups: readonly string[];
}

/**
 * 用来识别"改名"的**单位后缀**。
 *
 * 只列 ArduPilot 参数名里真实出现过的单位后缀（`_M`/`_CM`/`_MS`/`_DEG`…），
 * 不做"去掉任意尾串"的模糊匹配：`BATT_LOW_VOLT` 与 `BATT_LOW_VOLTAGE` 这类**真的换了词**
 * 的情况不该被猜成同一条 —— 猜错的代价是给用户一条含义不对的中文说明，
 * 比"没有中文"严重得多。所以这份报告只报"疑似"，最终由人确认。
 *
 * 顺序只是为了让报告的输出稳定（便于两次运行之间 diff），不影响判定。
 */
export const PARAM_UNIT_SUFFIXES: readonly string[] = Object.freeze([
  '_MCM',
  '_MAH',
  '_MPS',
  '_MPSS',
  '_MA',
  '_MV',
  '_MW',
  '_MS',
  '_M',
  '_CM',
  '_MM',
  '_KM',
  '_US',
  '_S',
  '_MIN',
  '_HZ',
  '_DEGS',
  '_DEG',
  '_RADS',
  '_RAD',
  '_RPM',
  '_PCT',
  '_HPA',
  '_PA',
  '_BAR',
  '_V',
  '_A',
  '_W',
  '_C',
  '_GS',
  '_KT',
  '_NM',
  '_FT',
  '_MILES',
  '_SATS',
  '_DB',
  '_RATIO',
  '_N',
]);

/**
 * 比对译表与一份元数据索引，报出**未覆盖**与**疑似改名**。
 *
 * 纯函数：只依赖传入的索引与译表，不联网、不读盘、不看时间。
 * `translations` 传 null 表示只核对内置种子表（与建索引时的合并规则一致，
 * 见 `mergeParamTranslations` —— 两处共用同一份合并逻辑，不会出现"审计说覆盖了、
 * 界面却没有中文"这种自相矛盾）。
 *
 * 代价：会**建模全部条目**（`index.get()` 会构造枚举/位掩码表，见 `metadata.ts` 的实测数字）。
 * 它是"批量审计 / 离线核对"用的，不在界面主路径上，所以这个代价可以接受；
 * 反过来，若把它接进渲染路径，就会把"惰性建模"这个设计直接废掉。
 */
export function auditTranslationCoverage(
  index: ParamMetadataIndex,
  translations: ParamTranslations | null = null,
): TranslationCoverageAudit {
  const { params: paramsZh, groups: groupsZh } = mergeParamTranslations(translations);
  const names = index.names();
  const present = new Set(names);

  if (index.source === 'empty' || names.length === 0) {
    return {
      status: 'unverified',
      source: index.source,
      unverifiedReason:
        index.unavailableReason ?? '没有可用的参数元数据（拿不到上游 pdef，未能与译表核对）',
      paramCount: names.length,
      translatedCount: 0,
      untranslated: [],
      staleTranslations: [],
      suspectedRenames: [],
      untranslatedGroups: [],
      staleGroups: [],
    };
  }

  const untranslated: string[] = [];
  let translatedCount = 0;
  for (const name of names) {
    // 判据只有一个：界面最终显示的那份中文有没有（`descriptionZh`）。
    // 换个判据（例如"译表里有没有这个键"）就会出现"报告说覆盖了、界面却是英文"的假绿
    const zh = index.get(name)?.descriptionZh ?? null;
    if (zh === null) untranslated.push(name);
    else translatedCount += 1;
  }

  const staleTranslations: string[] = [];
  const suspectedRenames: ParamRenameSuspicion[] = [];
  for (const key of Object.keys(paramsZh)) {
    if (present.has(key)) continue;
    const candidates = renameCandidatesOf(key, present);
    if (candidates.length === 0) staleTranslations.push(key);
    else suspectedRenames.push(...candidates);
  }

  // 输出顺序固定：脚本要能对两次运行的结果做 diff，否则"上游又改了一个名"会被淹没在噪声里
  suspectedRenames.sort((left, right) => {
    if (left.translationKey !== right.translationKey) {
      return left.translationKey < right.translationKey ? -1 : 1;
    }
    if (left.upstreamName !== right.upstreamName) {
      return left.upstreamName < right.upstreamName ? -1 : 1;
    }
    return left.suffix < right.suffix ? -1 : left.suffix > right.suffix ? 1 : 0;
  });
  staleTranslations.sort();
  untranslated.sort();

  const groups = index.groups();
  const groupIds = new Set(groups.map((group) => group.id));
  const untranslatedGroups: string[] = [];
  for (const group of groups) {
    if (groupsZh[group.id] === undefined) untranslatedGroups.push(group.id);
  }
  // `''`（未分组）不算"过时"：它是界面上永远存在的伪分组，
  // 而"这份 pdef 恰好没有未分组参数"不是译表的问题
  const staleGroups = Object.keys(groupsZh).filter((id) => id.length > 0 && !groupIds.has(id));
  untranslatedGroups.sort();
  staleGroups.sort();

  return {
    status: 'verified',
    source: index.source,
    unverifiedReason: null,
    paramCount: names.length,
    translatedCount,
    untranslated: Object.freeze(untranslated),
    staleTranslations: Object.freeze(staleTranslations),
    suspectedRenames: Object.freeze(suspectedRenames),
    untranslatedGroups: Object.freeze(untranslatedGroups),
    staleGroups: Object.freeze(staleGroups),
  };
}

/** 译表里的键在上游找不到时，看看它是不是"只是被加了/去了单位后缀" */
function renameCandidatesOf(
  translationKey: string,
  present: ReadonlySet<string>,
): ParamRenameSuspicion[] {
  const out: ParamRenameSuspicion[] = [];
  for (const suffix of PARAM_UNIT_SUFFIXES) {
    const longer = `${translationKey}${suffix}`;
    if (present.has(longer)) {
      out.push({
        translationKey,
        upstreamName: longer,
        suffix,
        direction: 'upstream-added-suffix',
      });
    }
    if (translationKey.length > suffix.length && translationKey.endsWith(suffix)) {
      const shorter = translationKey.slice(0, -suffix.length);
      if (present.has(shorter)) {
        out.push({
          translationKey,
          upstreamName: shorter,
          suffix,
          direction: 'upstream-dropped-suffix',
        });
      }
    }
  }
  return out;
}
